import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Buildings, Export, MagnifyingGlass, Printer, PencilSimple, Trash } from "@phosphor-icons/react";
import { exportCSV } from "@/hooks/useTableFeatures";

const fmt = (n) => {
  if (!n && n !== 0) return "$0";
  if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`;
  return `$${Number(n).toLocaleString()}`;
};
const fmtFull = (n) => (!n && n !== 0) ? "$0" : `$${Number(n).toLocaleString()}`;

export default function ClientFundingReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expandedClient, setExpandedClient] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);

  const loadReport = () => {
    setLoading(true);
    api.get("/reports/client-funding?start_year=2026&end_year=2030")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load report"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadReport(); }, []);

  const handleEdit = (r, e) => {
    e.stopPropagation();
    setEditForm({
      id: r.client_id,
      name: r.school_name,
      enrollment: r.enrollment,
      nslp: r.nslp,
      discount_rate: r.discount_pct,
      c2_budget_estimate: r.c2_budget_estimate,
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editForm) return;
    try {
      const current = (await api.get(`/clients/${editForm.id}`)).data;
      await api.put(`/clients/${editForm.id}`, {
        ...current,
        enrollment: editForm.enrollment,
        nslp: editForm.nslp,
        discount_rate: editForm.discount_rate,
        c2_budget_estimate: editForm.c2_budget_estimate,
      });
      toast.success("Client updated");
      setEditOpen(false);
      loadReport();
    } catch { toast.error("Failed to update"); }
  };

  const handleDelete = async (r, e) => {
    e.stopPropagation();
    if (!window.confirm(`Remove ${r.school_name} from report? This deletes the client record.`)) return;
    try {
      await api.delete(`/clients/${r.client_id}`);
      toast.success("Client deleted");
      loadReport();
    } catch { toast.error("Failed to delete"); }
  };

  if (loading) return <div className="p-8 flex items-center justify-center h-full"><p className="text-sm text-[#6B7280]">Generating report...</p></div>;
  if (!data) return null;

  const { rows, totals, years } = data;
  const filtered = search
    ? rows.filter((r) => [r.school_name, r.ben].some((v) => (v || "").toLowerCase().includes(search.toLowerCase())))
    : rows;

  const csvCols = [
    { label: "School Name", key: "school_name" }, { label: "BEN", key: "ben" },
    { label: "Enrollment", key: "enrollment" }, { label: "NSLP %", key: "nslp" },
    { label: "Discount %", key: "discount_pct" }, { label: "Est. C2 Budget", key: "c2_budget_estimate" },
    { label: "Cat 1 Approved", key: "c1_funding_approved" }, { label: "Cat 1 Disbursed", key: "c1_disbursed" },
    { label: "Cat 2 Committed", key: "c2_committed_total" }, { label: "Cat 2 Discount Amt", key: "c2_committed_discount" },
    { label: "Cat 2 Disbursed", key: "c2_disbursed" }, { label: "Total Approved", key: "total_approved" },
    { label: "Total Disbursed", key: "total_disbursed" },
  ];

  const chartData = years.map((y) => {
    let c1 = 0, c2 = 0, disb = 0;
    filtered.forEach((r) => {
      const yd = r.by_year?.[y];
      if (yd) { c1 += yd.c1_approved; c2 += yd.c2_committed; disb += yd.disbursed; }
    });
    return { year: `FY${y}`, "Cat 1 Approved": c1, "Cat 2 Committed": c2, "Disbursed": disb };
  });

  return (
    <div className="p-6" data-testid="client-funding-report">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-1">Reports</p>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Client Funding Report
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">FY {years[0]}-{years[years.length - 1]} | Category 1 & 2 Funding, Disbursements, C2 Budget</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="print-report-btn" onClick={() => window.print()} className="flex items-center gap-1.5 bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-3 py-2 text-xs transition-colors">
            <Printer size={14} /> Print
          </button>
          <button data-testid="export-funding-report-btn" onClick={() => exportCSV(filtered, csvCols, "client_funding_report.csv")} className="flex items-center gap-1.5 bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-3 py-2 text-xs transition-colors">
            <Export size={14} /> Export CSV
          </button>
        </div>
      </div>

      {/* Grand Totals */}
      <div className="border border-gray-200 mb-6">
        <div className="bg-[#002FA7] px-5 py-3">
          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-white/70">Portfolio Totals - {totals.client_count} Clients</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0">
          <div className="p-4 border-r border-b border-gray-200 bg-white">
            <span className="text-[9px] uppercase tracking-[0.15em] font-semibold text-[#6B7280]">Cat 1 Approved</span>
            <p className="text-xl font-bold text-[#002FA7] mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{fmt(totals.total_c1_approved)}</p>
            <span className="text-[10px] text-[#6B7280]">Disbursed: {fmt(totals.total_c1_disbursed)}</span>
          </div>
          <div className="p-4 border-r border-b border-gray-200 bg-white">
            <span className="text-[9px] uppercase tracking-[0.15em] font-semibold text-[#6B7280]">Cat 2 Committed</span>
            <p className="text-xl font-bold text-[#00B341] mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{fmt(totals.total_c2_committed)}</p>
            <span className="text-[10px] text-[#6B7280]">Discount Amt: {fmt(totals.total_c2_discount)}</span>
          </div>
          <div className="p-4 border-r border-b border-gray-200 bg-white">
            <span className="text-[9px] uppercase tracking-[0.15em] font-semibold text-[#6B7280]">Cat 2 Disbursed</span>
            <p className="text-xl font-bold text-[#D97706] mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{fmt(totals.total_c2_disbursed)}</p>
            <span className="text-[10px] text-[#6B7280]">C2 Budget: {fmt(totals.total_c2_budget)}</span>
          </div>
          <div className="p-4 border-b border-gray-200 bg-white">
            <span className="text-[9px] uppercase tracking-[0.15em] font-semibold text-[#6B7280]">All Disbursed</span>
            <p className="text-xl font-bold text-[#1A1D20] mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{fmt(totals.total_all_disbursed)}</p>
            <span className="text-[10px] text-[#6B7280]">of {fmt(totals.total_all_approved)} approved</span>
          </div>
        </div>
      </div>

      {/* Chart */}
      {chartData.some((d) => d["Cat 1 Approved"] > 0 || d["Cat 2 Committed"] > 0) && (
        <div className="bg-white border border-gray-200 p-6 mb-6" data-testid="funding-year-chart">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-4">Funding by Year</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData} barCategoryGap="25%">
              <XAxis dataKey="year" tick={{ fontSize: 12, fill: "#6B7280" }} axisLine={{ stroke: "#E5E7EB" }} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} tickFormatter={(v) => fmt(v)} />
              <Tooltip formatter={(v) => fmtFull(v)} contentStyle={{ border: "1px solid #E5E7EB", borderRadius: 0, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Cat 1 Approved" fill="#002FA7" />
              <Bar dataKey="Cat 2 Committed" fill="#00B341" />
              <Bar dataKey="Disbursed" fill="#D97706" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Search */}
      <div className="mb-4 relative max-w-sm">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
        <Input data-testid="search-funding-report" placeholder="Search school name or BEN..." value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-none pl-9" />
      </div>

      {/* Report Table */}
      <div className="border border-gray-200 bg-white overflow-x-auto" data-testid="funding-report-table">
        <Table>
          <TableHeader>
            <TableRow className="data-table-header">
              <TableHead className="py-2.5 px-3 text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold sticky left-0 bg-[#F8F9FA] z-10 min-w-[180px]">School Name</TableHead>
              <TableHead className="py-2.5 px-3 text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">BEN</TableHead>
              <TableHead className="py-2.5 px-3 text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">Enrollment</TableHead>
              <TableHead className="py-2.5 px-3 text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">NSLP %</TableHead>
              <TableHead className="py-2.5 px-3 text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">Discount %</TableHead>
              <TableHead className="py-2.5 px-3 text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">Est. C2 Budget</TableHead>
              <TableHead className="py-2.5 px-3 text-[10px] uppercase tracking-wider text-[#002FA7] font-semibold border-l-2 border-l-[#002FA7]">Cat 1 Approved</TableHead>
              <TableHead className="py-2.5 px-3 text-[10px] uppercase tracking-wider text-[#002FA7] font-semibold">Cat 1 Disbursed</TableHead>
              <TableHead className="py-2.5 px-3 text-[10px] uppercase tracking-wider text-[#00B341] font-semibold border-l-2 border-l-[#00B341]">Cat 2 Committed</TableHead>
              <TableHead className="py-2.5 px-3 text-[10px] uppercase tracking-wider text-[#00B341] font-semibold">Cat 2 Discount Amt</TableHead>
              <TableHead className="py-2.5 px-3 text-[10px] uppercase tracking-wider text-[#00B341] font-semibold">Cat 2 Disbursed</TableHead>
              <TableHead className="py-2.5 px-3 text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold border-l border-gray-300">Total</TableHead>
              <TableHead className="py-2.5 px-3 text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={13} className="text-center py-8 text-[#6B7280] text-sm">No client data found.</TableCell></TableRow>
            ) : (
              <>
                {filtered.map((r) => (
                  <TableRow
                    key={r.client_id}
                    className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setExpandedClient(expandedClient === r.client_id ? null : r.client_id)}
                    data-testid={`funding-row-${r.client_id}`}
                  >
                    <TableCell className="py-2.5 px-3 text-sm font-medium text-[#1A1D20] sticky left-0 bg-white z-10 min-w-[180px]">
                      <div className="flex items-center gap-2">
                        <Buildings size={14} className="text-[#002FA7] flex-shrink-0" />
                        {r.school_name || "-"}
                      </div>
                    </TableCell>
                    <TableCell className="py-2.5 px-3 font-mono text-xs text-[#1A1D20]">{r.ben || "-"}</TableCell>
                    <TableCell className="py-2.5 px-3 text-xs text-[#1A1D20]">{r.enrollment > 0 ? r.enrollment.toLocaleString() : "-"}</TableCell>
                    <TableCell className="py-2.5 px-3 text-xs text-[#1A1D20]">{r.nslp > 0 ? `${r.nslp}%` : "-"}</TableCell>
                    <TableCell className="py-2.5 px-3 text-xs font-medium text-[#002FA7]">{r.discount_pct > 0 ? `${r.discount_pct}%` : "-"}</TableCell>
                    <TableCell className="py-2.5 px-3 text-xs font-medium">{r.c2_budget_estimate > 0 ? fmtFull(r.c2_budget_estimate) : "-"}</TableCell>
                    <TableCell className="py-2.5 px-3 text-xs font-medium text-[#002FA7] border-l-2 border-l-[#002FA7]/20">{r.c1_funding_approved > 0 ? fmtFull(r.c1_funding_approved) : "-"}</TableCell>
                    <TableCell className="py-2.5 px-3 text-xs">{r.c1_disbursed > 0 ? fmtFull(r.c1_disbursed) : "-"}</TableCell>
                    <TableCell className="py-2.5 px-3 text-xs font-medium text-[#00B341] border-l-2 border-l-[#00B341]/20">{r.c2_committed_total > 0 ? fmtFull(r.c2_committed_total) : "-"}</TableCell>
                    <TableCell className="py-2.5 px-3 text-xs">{r.c2_committed_discount > 0 ? fmtFull(r.c2_committed_discount) : "-"}</TableCell>
                    <TableCell className="py-2.5 px-3 text-xs">{r.c2_disbursed > 0 ? fmtFull(r.c2_disbursed) : "-"}</TableCell>
                    <TableCell className="py-2.5 px-3 text-xs font-bold text-[#1A1D20] border-l border-gray-200">{r.total_approved > 0 ? fmtFull(r.total_approved) : "-"}</TableCell>
                    <TableCell className="py-2.5 px-3">
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button data-testid={`edit-funding-${r.client_id}`} onClick={(e) => handleEdit(r, e)} className="p-1.5 hover:bg-gray-100 transition-colors"><PencilSimple size={14} className="text-[#6B7280]" /></button>
                        <button data-testid={`delete-funding-${r.client_id}`} onClick={(e) => handleDelete(r, e)} className="p-1.5 hover:bg-red-50 transition-colors"><Trash size={14} className="text-[#FF2A00]" /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals row */}
                <TableRow className="bg-[#F8F9FA] border-t-2 border-gray-300">
                  <TableCell className="py-3 px-3 text-xs font-bold text-[#1A1D20] sticky left-0 bg-[#F8F9FA] z-10">TOTALS</TableCell>
                  <TableCell className="py-3 px-3"></TableCell>
                  <TableCell className="py-3 px-3"></TableCell>
                  <TableCell className="py-3 px-3"></TableCell>
                  <TableCell className="py-3 px-3"></TableCell>
                  <TableCell className="py-3 px-3 text-xs font-bold">{fmtFull(totals.total_c2_budget)}</TableCell>
                  <TableCell className="py-3 px-3 text-xs font-bold text-[#002FA7] border-l-2 border-l-[#002FA7]/20">{fmtFull(totals.total_c1_approved)}</TableCell>
                  <TableCell className="py-3 px-3 text-xs font-bold">{fmtFull(totals.total_c1_disbursed)}</TableCell>
                  <TableCell className="py-3 px-3 text-xs font-bold text-[#00B341] border-l-2 border-l-[#00B341]/20">{fmtFull(totals.total_c2_committed)}</TableCell>
                  <TableCell className="py-3 px-3 text-xs font-bold">{fmtFull(totals.total_c2_discount)}</TableCell>
                  <TableCell className="py-3 px-3 text-xs font-bold">{fmtFull(totals.total_c2_disbursed)}</TableCell>
                  <TableCell className="py-3 px-3 text-xs font-bold text-[#1A1D20] border-l border-gray-200">{fmtFull(totals.total_all_approved)}</TableCell>
                  <TableCell className="py-3 px-3"></TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Expanded year breakdown with enrollment/NSLP/discount */}
      {expandedClient && (() => {
        const r = filtered.find((x) => x.client_id === expandedClient);
        if (!r) return null;
        const activeYears = years.filter((y) => {
          const yd = r.by_year?.[y];
          return yd && (yd.c1_approved > 0 || yd.c2_committed > 0 || yd.disbursed > 0 || yd.frn_count > 0);
        });
        const displayYears = activeYears.length > 0 ? activeYears : years;
        return (
          <div className="mt-4 bg-white border border-[#002FA7]/20 p-5" data-testid="year-breakdown">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002FA7]">{r.school_name} - Year-by-Year Breakdown</p>
              <button onClick={(e) => handleEdit(r, e)} className="text-xs text-[#002FA7] hover:underline flex items-center gap-1"><PencilSimple size={12} /> Edit Client</button>
            </div>
            <div className={`grid gap-0 border border-gray-200`} style={{ gridTemplateColumns: `repeat(${Math.min(displayYears.length, 6)}, 1fr)` }}>
              {displayYears.map((y) => {
                const yd = r.by_year?.[y] || {};
                return (
                  <div key={y} className="p-3 border-r border-gray-200 last:border-r-0">
                    <span className="text-xs font-bold text-[#1A1D20] block mb-2">FY{y}</span>
                    <div className="space-y-1.5">
                      <div className="bg-[#F8F9FA] p-1.5 mb-2">
                        <div className="grid grid-cols-3 gap-1">
                          <div><span className="text-[8px] text-[#6B7280] block">Enrollment</span><p className="text-[10px] font-medium">{yd.enrollment > 0 ? yd.enrollment.toLocaleString() : "-"}</p></div>
                          <div><span className="text-[8px] text-[#6B7280] block">NSLP</span><p className="text-[10px] font-medium">{yd.nslp > 0 ? `${yd.nslp}%` : "-"}</p></div>
                          <div><span className="text-[8px] text-[#6B7280] block">Discount</span><p className="text-[10px] font-medium text-[#002FA7]">{yd.discount_rate > 0 ? `${yd.discount_rate}%` : "-"}</p></div>
                        </div>
                      </div>
                      <div><span className="text-[9px] text-[#6B7280]">C1 Approved</span><p className="text-xs font-medium text-[#002FA7]">{yd.c1_approved > 0 ? fmtFull(yd.c1_approved) : "-"}</p></div>
                      <div><span className="text-[9px] text-[#6B7280]">C2 Committed</span><p className="text-xs font-medium text-[#00B341]">{yd.c2_committed > 0 ? fmtFull(yd.c2_committed) : "-"}</p></div>
                      <div><span className="text-[9px] text-[#6B7280]">Disbursed</span><p className="text-xs font-medium">{yd.disbursed > 0 ? fmtFull(yd.disbursed) : "-"}</p></div>
                      {yd.frn_count > 0 && (
                        <div className="pt-1 border-t border-gray-100">
                          <span className="text-[8px] text-[#6B7280]">{yd.frn_count} FRN{yd.frn_count > 1 ? "s" : ""} | {yd.approved_count} approved</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-none sm:max-w-[480px]">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Edit Client Data</DialogTitle></DialogHeader>
          {editForm && (
            <div className="grid gap-4 py-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">School Name</label>
                <Input value={editForm.name} disabled className="rounded-none bg-gray-50" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Enrollment</label>
                  <Input data-testid="edit-enrollment" type="number" value={editForm.enrollment} onChange={(e) => setEditForm((f) => ({ ...f, enrollment: parseInt(e.target.value) || 0 }))} className="rounded-none" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">NSLP (%)</label>
                  <Input data-testid="edit-nslp" type="number" value={editForm.nslp} onChange={(e) => setEditForm((f) => ({ ...f, nslp: parseFloat(e.target.value) || 0 }))} className="rounded-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Discount Rate (%)</label>
                  <Input data-testid="edit-discount" type="number" value={editForm.discount_rate} onChange={(e) => setEditForm((f) => ({ ...f, discount_rate: parseFloat(e.target.value) || 0 }))} className="rounded-none" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Est. C2 Budget ($)</label>
                  <Input data-testid="edit-c2budget" type="number" value={editForm.c2_budget_estimate} onChange={(e) => setEditForm((f) => ({ ...f, c2_budget_estimate: parseFloat(e.target.value) || 0 }))} className="rounded-none" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditOpen(false)} className="bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-4 py-2 text-sm">Cancel</button>
            <button data-testid="save-funding-edit-btn" onClick={saveEdit} className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-4 py-2 text-sm">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
